import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Calendar } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  // Navigation structure with dropdowns
  const navigation = [
    {
      name: 'About',
      dropdown: true,
      items: [
        { name: 'Our Story', href: '/about' },
        { name: 'Why Houston', href: '/why-houston' },
        { name: 'Team', href: '/team' }
      ]
    },
    {
      name: 'Investments',
      dropdown: true,
      items: [
        { name: 'Investment Strategy', href: '/strategy' },
        { name: 'Fund Terms', href: '/fund-terms' },
        { name: 'Portfolio', href: '/portfolio' },
        { name: 'Track Record', href: '/track-record' },
        { name: 'Investment Process', href: '/investment-process' }
      ]
    },
    {
      name: 'For Investors',
      dropdown: true,
      items: [
        { name: 'High Net Worth Individuals', href: '/investors/high-net-worth' },
        { name: 'Family Offices', href: '/investors/family-offices' },
        { name: 'Institutional Investors', href: '/investors/institutional' },
        { name: 'FAQ', href: '/faq' }
      ]
    },
    {
      name: 'Lending',
      href: '/lending',
      dropdown: false
    },
    {
      name: 'Resources',
      dropdown: true,
      items: [
        { name: 'Insights', href: '/insights' },
        { name: 'Case Studies', href: '/case-studies' },
        { name: 'Market Reports', href: '/market-reports' },
        { name: 'Investor Resources', href: '/resources' }
      ]
    },
    {
      name: 'Press & Media',
      href: '/press',
      dropdown: false
    },
    {
      name: 'Contact',
      href: '/contact',
      dropdown: false
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleDropdownEnter = (name) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const toggleMobileDropdown = (name) => {
    setMobileOpenDropdown(mobileOpenDropdown === name ? null : name);
  };

  // Check if we're on the homepage (for anchor links)
  const isHomepage = location.pathname === '/';

  // Handle navigation - for pages that don't exist yet, scroll to section on homepage
  const handleNavigation = (href, e) => {
    // If the page doesn't exist yet and we're on homepage, try scrolling to section
    if (isHomepage && href.startsWith('/')) {
      const sectionId = href.replace('/', '').replace(/-/g, '');
      const section = document.getElementById(sectionId);
      if (section) {
        e.preventDefault();
        section.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
        return;
      }
    }
    setMobileMenuOpen(false);
  };

  const dropdownVariants = {
    hidden: { 
      opacity: 0, 
      y: 10,
      transition: { duration: 0.15 }
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.2, ease: 'easeOut' }
    }
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? 'glass-strong border-b border-white/10' 
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Top breathing room spacer */}
        <div className="h-2 lg:h-3" />
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-12 xl:px-16">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Logo */}
            <Link to="/">
              <motion.div 
                className="flex items-center gap-2 group cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <img 
                  src={companyInfo.logo} 
                  alt="ASAP Capital" 
                  className="h-20 md:h-24 lg:h-28 w-auto object-contain"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navigation.map((item, index) => (
                <div 
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.dropdown && handleDropdownEnter(item.name)}
                  onMouseLeave={() => item.dropdown && handleDropdownLeave()}
                >
                  {item.dropdown ? (
                    <>
                      {/* Dropdown Trigger */}
                      <motion.button
                        className={`flex items-center gap-1 px-3 py-2 text-white/60 hover:text-[#D4AF37] text-sm font-medium transition-colors duration-300 tracking-wide ${
                          openDropdown === item.name ? 'text-[#D4AF37]' : ''
                        }`}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.name}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${
                          openDropdown === item.name ? 'rotate-180' : ''
                        }`} />
                      </motion.button>

                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {openDropdown === item.name && (
                          <motion.div
                            className="absolute top-full left-0 mt-1 min-w-[220px] py-2 rounded-xl glass-strong border border-white/10 shadow-xl shadow-black/20"
                            variants={dropdownVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            onMouseEnter={() => handleDropdownEnter(item.name)}
                            onMouseLeave={handleDropdownLeave}
                          >
                            {item.items.map((subItem, subIndex) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className="block px-4 py-2.5 text-sm text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-colors duration-200"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <motion.span
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: subIndex * 0.03 }}
                                >
                                  {subItem.name}
                                </motion.span>
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    // Regular Link
                    <Link to={item.href}>
                      <motion.span
                        className="relative block px-3 py-2 text-white/60 hover:text-[#D4AF37] text-sm font-medium transition-colors duration-300 tracking-wide"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        whileHover={{ y: -1 }}
                      >
                        {item.name}
                      </motion.span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <motion.div 
              className="hidden lg:block"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/contact">
                <button className="cta-contact-nav">
                  <Calendar className="h-4 w-4" />
                  Schedule Consultation
                </button>
              </Link>
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.button
              className="lg:hidden w-9 h-9 glass border border-white/10 rounded-full flex items-center justify-center text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              whileHover={{ scale: 1.05, borderColor: 'rgba(212, 175, 55, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-4 w-4" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-4 w-4" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-[#0A1628]/80 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              className="fixed top-0 right-0 w-[300px] h-full glass-strong border-l border-white/10 z-50 lg:hidden overflow-y-auto"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className="flex flex-col gap-2 p-6 mt-16">
                {navigation.map((item, index) => (
                  <div key={item.name}>
                    {item.dropdown ? (
                      <>
                        {/* Mobile Dropdown Trigger */}
                        <motion.button
                          className="w-full flex items-center justify-between text-white/70 hover:text-[#D4AF37] text-base font-medium py-2 transition-colors duration-300"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => toggleMobileDropdown(item.name)}
                        >
                          {item.name}
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                            mobileOpenDropdown === item.name ? 'rotate-180' : ''
                          }`} />
                        </motion.button>

                        {/* Mobile Dropdown Items */}
                        <AnimatePresence>
                          {mobileOpenDropdown === item.name && (
                            <motion.div
                              className="pl-4 border-l border-white/10 ml-2 mt-1 mb-2"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {item.items.map((subItem, subIndex) => (
                                <Link
                                  key={subItem.name}
                                  to={subItem.href}
                                  className="block py-2 text-sm text-white/50 hover:text-[#D4AF37] transition-colors duration-200"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  <motion.span
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: subIndex * 0.03 }}
                                  >
                                    {subItem.name}
                                  </motion.span>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      // Mobile Regular Link
                      <Link
                        to={item.href}
                        className="block text-white/70 hover:text-[#D4AF37] text-base font-medium py-2 transition-colors duration-300"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <motion.span
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ x: 5 }}
                        >
                          {item.name}
                        </motion.span>
                      </Link>
                    )}
                  </div>
                ))}
                
                {/* Mobile CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-4"
                >
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    <button className="cta-contact w-full px-5 py-2.5 rounded-full">
                      <Calendar className="h-4 w-4" />
                      Schedule Consultation
                    </button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
